import {
  type ChangeEvent,
  memo,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Box,
  type BoxProps,
  Checkbox,
  type SystemStyleObject,
} from "@hightouch/htui";

import {
  getPageScrollMargin,
  usePageScroll,
  usePageVirtualizer,
} from "src/components/layout/page-scroll";
import { Private } from "src/components/private";
import { SortMenu } from "src/components/sort-menu";
import { usePermissionContext } from "src/components/permission/permission-context";
import type { OrderBy } from "src/graphql";
import type { SortOption } from "src/ui/table/use-table-config";
import { Cell, HeaderCell, TableCell } from "./cells";
import { Placeholder, type PlaceholderContent } from "./placeholder";
import { Row } from "./row";
import { DefaultPageContainerPadding } from "src/components/layout/page-container";
import { isNavFolding, onNavFoldEnd } from "src/components/layout/nav/nav-fold";
import { SkeletonContextProvider, maskCss } from "src/components/skeletons";

export type TableColumnVariant = "numeric" | "center";

export type TableColumn<RowData extends Record<string, any> = any> = {
  name?: string;
  header?: () => ReactElement;
  headerSx?: SystemStyleObject;
  cell: (
    row: RowData,
    index: number,
    selected: boolean,
  ) => ReactNode | ReactElement;
  cellSx?:
    | SystemStyleObject
    | ((row: RowData) => SystemStyleObject | undefined);
  min?: string;
  max?: string;
  whitespace?: "unset" | undefined;
  divider?: boolean;
  disabled?: (row: RowData) => boolean | string;
  sortDirection?: OrderBy | null;
  onClick?(): void;
  breakpoint?: "lg" | "md" | "sm";
  variant?: TableColumnVariant;
  /**
   * Cells that paint their own background. Under a `highlightColor`, neither
   * the highlight nor the hover fill repaints them; inert under the default
   * tinted highlight, which layers over a cell instead of replacing it.
   */
  skipHighlight?: (row: RowData) => boolean;
  /** Fires for the whole cell, padding included, not just its content. */
  onCellMouseEnter?: (row: RowData) => void;
  onCellMouseLeave?: (row: RowData) => void;

  // Show a custom skeleton component when loading
  skeleton?: ReactElement;
};

export type RowClickHandler<RowData extends Record<string, any>> = (
  row: RowData,
  event: MouseEvent,
) => unknown;

export type TableProps<RowData extends Record<string, any>> = {
  columns: Array<TableColumn<RowData>>;
  data: Array<RowData> | undefined;
  onRowClick?: RowClickHandler<RowData>;
  onSelect?: (key: any) => void;
  selectedRows?: Array<any>;
  width?: string;
  defaultMin?: string;
  defaultMax?: string;
  error?: boolean;
  placeholder?: PlaceholderContent;
  primaryKey?: keyof RowData;
  /**
   * A function returning `true` or a truthy string on a given row will disable the row.
   * If the row is selectable, the string will be displayed as a tooltip on the checkbox
   */
  disabled?: (row: RowData) => boolean | string;
  /**
   * Controls whether a row's checkbox is enabled without affecting row opacity or click handling.
   * `true` → selectable (default when omitted), `false` → checkbox disabled, `string` → disabled with tooltip.
   */
  selectable?: (row: RowData) => boolean | string;
  /** Names each row's checkbox for screen readers. */
  selectLabel?: (row: RowData) => string;
  headerHeight?: string;
  rowHeight?: string;
  /** Rows to tint as pointed-at, which is not the same as selected: no
   *  checkbox is ticked and each cell keeps its own background. A list is for a
   *  highlight that comes from outside the table, where one hovered thing can
   *  address several rows. */
  highlight?: number | string | ReadonlyArray<number | string>;
  /**
   * Paints highlighted rows an opaque color rather than tinting them. Columns
   * carrying a background of their own opt out via `skipHighlight`.
   */
  highlightColor?: BoxProps["bg"];
  /** Row hover, for a surface that mirrors the hovered row somewhere else. */
  onRowMouseEnter?: (row: RowData) => void;
  onRowMouseLeave?: (row: RowData) => void;
  loading?: boolean;
  showHeaders?: boolean;
  sortingEnabled?: boolean;
  scrollable?: boolean;
  scrollableX?: boolean;
  scrollableY?: boolean;
  top?: number | string;
  /** @deprecated use sort from useTableConfig */
  sortOptions?: SortOption<any>[];
  isDense?: boolean;
  isPrivate?: boolean;
  alignFirstHeader?: boolean;
  backgroundColor?: string;
  renderExpandedRow?: (row: RowData, rowIndex: number) => ReactNode;
  /** The expanded rows' primary keys, for a caller that renders its own toggle in a cell; the table then adds no toggle column. */
  expandedRowKeys?: ReadonlyArray<string | number>;
  hideLastRowBorder?: boolean;
  // Mount only rows near the viewport. Off by default, enabled by PageTable. Ignored for expandable, inner-scroll, or short tables.
  // Caveat: find-in-page and screen readers only see mounted rows, and minmax columns can resize as row content mounts.
  virtualize?: boolean;
};

export function Table<RowData extends Record<string, any>>({
  primaryKey = "id",
  columns: propColumns,
  data,
  onRowClick,
  onSelect,
  onRowMouseEnter,
  onRowMouseLeave,
  selectedRows,
  width = "100%",
  defaultMin = "0",
  defaultMax = "1fr",
  placeholder,
  error,
  headerHeight = "52px",
  rowHeight,
  disabled,
  selectable,
  selectLabel,
  highlight,
  highlightColor,
  loading,
  sortingEnabled = true,
  showHeaders = true,
  scrollable = false,
  scrollableX = false,
  scrollableY = false,
  isDense = false,
  top,
  sortOptions,
  isPrivate = false,
  alignFirstHeader = false,
  backgroundColor = "white",
  renderExpandedRow,
  expandedRowKeys,
  hideLastRowBorder = false,
  virtualize = false,
}: Readonly<TableProps<RowData>>) {
  const isEmpty = !loading && data?.length === 0;
  const permission = usePermissionContext();

  const supportsSelection =
    Boolean(selectedRows && onSelect) && !permission?.unauthorized;
  const tableRef = useRef<HTMLDivElement | null>(null);
  const widthBreakpoint = useWidthBreakpoint(tableRef);
  const sortable = Boolean(sortOptions && sortOptions.length > 0);
  const expandable = Boolean(renderExpandedRow) && !expandedRowKeys;

  // Width is unknown until the first measurement, so every column shows until then.
  const isAboveLg = widthBreakpoint === undefined || widthBreakpoint === "lg";
  const isAboveMd = isAboveLg || widthBreakpoint === "md";
  const isAboveSm = isAboveMd || widthBreakpoint === "sm";

  const columns = useMemo(() => {
    return propColumns.filter((column) => {
      switch (column.breakpoint) {
        case "lg":
          return isAboveLg;
        case "md":
          return isAboveMd;
        case "sm":
          return isAboveSm;
        default:
          return true;
      }
    });
  }, [propColumns, isAboveLg, isAboveMd, isAboveSm]);

  const placeholderRows = useMemo(
    () => createPlaceholderData(primaryKey),
    [primaryKey],
  );

  const isPlaceholder = isEmpty || error;

  const dataLength = data?.length ?? 0;
  const isVirtualized =
    virtualize &&
    !loading &&
    !isPlaceholder &&
    !renderExpandedRow &&
    !scrollable &&
    !scrollableX &&
    !scrollableY &&
    dataLength > VIRTUALIZE_ROW_THRESHOLD;

  const renderRow = useCallback(
    (
      row: RowData,
      index: number,
      rowCount: number,
      measureRef?: (node: Element | null) => void,
    ) => {
      const isLastRow = index === rowCount - 1;
      return (
        <MemoizedTableRow
          key={row[primaryKey] ?? index}
          columns={columns as TableColumn<Record<string, any>>[]}
          disabled={disabled ? disabled(row) : false}
          selectable={selectable ? selectable(row) : true}
          checkboxLabel={selectLabel?.(row)}
          height={isDense ? "auto" : rowHeight}
          index={index}
          measureRef={measureRef}
          primaryKey={primaryKey as any}
          row={row}
          selected={Boolean(selectedRows?.includes(row[primaryKey]))}
          isHighlighted={isRowHighlighted(highlight, row[primaryKey])}
          highlightColor={highlightColor}
          onClick={onRowClick as any}
          onMouseEnter={onRowMouseEnter as any}
          onMouseLeave={onRowMouseLeave as any}
          onSelect={
            supportsSelection && onSelect ? (onSelect as any) : undefined
          }
          sortable={sortable}
          isDense={isDense}
          renderExpandedRow={loading ? undefined : (renderExpandedRow as any)}
          isExpanded={expandedRowKeys?.includes(row[primaryKey])}
          isLastRow={isLastRow && hideLastRowBorder}
        />
      );
    },
    [
      columns,
      highlight,
      highlightColor,
      onRowClick,
      onRowMouseEnter,
      onRowMouseLeave,
      onSelect,
      disabled,
      selectable,
      selectLabel,
      selectedRows,
      primaryKey,
      rowHeight,
      supportsSelection,
      isDense,
      renderExpandedRow,
      expandedRowKeys,
      loading,
      sortable,
      hideLastRowBorder,
    ],
  );

  // The virtualized path renders its own window of rows.
  const rows = useMemo(() => {
    if (isVirtualized) return undefined;
    const rowData = loading ? placeholderRows : data;
    return rowData?.map((row, index) => renderRow(row, index, rowData.length));
  }, [renderRow, loading, placeholderRows, data, isVirtualized]);

  const headerCells = useMemo(() => {
    return columns.map(
      (
        {
          header,
          headerSx,
          name,
          sortDirection,
          onClick,
          divider,
          variant,
        }: TableColumn,
        index,
      ) => {
        const key = name ? name : `header-column-${index}-cell`;
        const sortingProps = sortingEnabled
          ? {
              sortDirection,
              onClick,
            }
          : {};

        return (
          <HeaderCell
            key={key}
            top={top}
            isDense={isDense}
            divider={divider}
            {...sortingProps}
            alignFirstHeader={alignFirstHeader}
            backgroundColor={backgroundColor}
            sx={headerSx}
            height={headerHeight}
            variant={variant}
            isLoading={loading}
          >
            {header ? header() : name}
          </HeaderCell>
        );
      },
    );
  }, [
    backgroundColor,
    columns,
    top,
    isDense,
    loading,
    sortingEnabled,
    alignFirstHeader,
    headerHeight,
  ]);

  const canToggleRow = useCallback(
    (row: RowData) => {
      if (disabled?.(row)) return false;
      if (selectable && selectable(row) !== true) return false;
      return true;
    },
    [disabled, selectable],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const onSelectAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (typeof onSelect !== "function") {
        return;
      }

      const selectedUntoggleableRows =
        data?.filter(
          (row) =>
            !canToggleRow(row) && selectedRows?.includes(row[primaryKey]),
        ) ?? [];

      if (event.target.checked) {
        onSelect(
          [
            ...(data?.filter(canToggleRow) ?? []),
            ...selectedUntoggleableRows,
          ].map((row) => row[primaryKey]),
        );
      } else {
        onSelect(selectedUntoggleableRows.map((row) => row[primaryKey]));
      }
    },
    [data, onSelect, primaryKey, canToggleRow],
  );

  // Only the select-all checkbox reads these; the Set keeps it O(rows).
  const { enabledRowCount, enabledSelectedRowCount } = useMemo(() => {
    if (!supportsSelection || !data) {
      return { enabledRowCount: 0, enabledSelectedRowCount: 0 };
    }

    const selectedKeys = new Set(selectedRows);
    let enabled = 0;
    let enabledSelected = 0;

    for (const row of data) {
      if (!canToggleRow(row)) continue;
      enabled++;
      if (selectedKeys.has(row[primaryKey])) enabledSelected++;
    }

    return {
      enabledRowCount: enabled,
      enabledSelectedRowCount: enabledSelected,
    };
  }, [supportsSelection, data, selectedRows, canToggleRow, primaryKey]);

  return (
    <SkeletonContextProvider isLoading={Boolean(loading)}>
      <Private isDisabled={!isDense || !isPrivate}>
        <Box
          ref={tableRef as any}
          flex={isPlaceholder ? 1 : undefined}
          height={isPlaceholder ? "100%" : undefined}
          as="table"
          aria-rowcount={isVirtualized ? dataLength : undefined}
          overflow={isPlaceholder ? "hidden" : undefined}
          bg={backgroundColor}
          display={isPlaceholder ? "flex" : "grid"}
          p={isPlaceholder ? placeholder?.p : undefined}
          flexDirection="column"
          gridTemplateColumns={getGridTemplateColumns(
            [
              expandable && { max: "max-content" },
              supportsSelection && { min: "max-content", max: "max-content" },
              ...columns,
              sortable && { max: "max-content" },
            ].filter(Boolean),
            scrollable || scrollableY ? "min-content" : defaultMin,
            defaultMax,
          )}
          width={width}
          overflowX={scrollable || scrollableX ? "auto" : undefined}
          tabIndex={scrollable || scrollableX ? 0 : undefined} // Scrollable tables require keyboard access
          sx={{
            [`td:nth-child(1)`]: { pl: isDense ? undefined : 6 },
            [`td:nth-child(${columns.length + (expandable ? 2 : 1)}n)`]: {
              pr: isDense ? undefined : 6,
            },
            mask: !error && loading ? maskCss : undefined,
          }}
          // While loading, nothing should be clickable.
          // This also prevents hover styles from being triggered.
          pointerEvents={loading ? "none" : undefined}
        >
          {showHeaders && !isPlaceholder && (
            <Box as="thead" display="contents">
              <Box as="tr" display="contents">
                {expandable && <HeaderCell>{null}</HeaderCell>}
                {supportsSelection && (
                  <HeaderCell top={top} isLoading={loading}>
                    <Checkbox
                      aria-label="Select all"
                      isChecked={Boolean(
                        data &&
                          data?.length > 0 &&
                          enabledSelectedRowCount === enabledRowCount,
                      )}
                      isIndeterminate={Boolean(
                        data &&
                          data?.length > 0 &&
                          (selectedRows?.length ?? 0) > 0 &&
                          enabledSelectedRowCount < enabledRowCount,
                      )}
                      onChange={onSelectAll}
                    />
                  </HeaderCell>
                )}
                {headerCells}
                {sortable && (
                  <HeaderCell isLoading={loading} top={top} isSortMenu>
                    <SortMenu options={sortOptions} />
                  </HeaderCell>
                )}
              </Box>
            </Box>
          )}

          <Box as="tbody" display="contents" position="relative">
            {isEmpty || error ? (
              <Box as="tr" display="contents" position="relative">
                <Box
                  as="td"
                  colSpan={columns.length + 1}
                  height="100%"
                  display="flex"
                  alignItems="center"
                  p={`${DefaultPageContainerPadding.X} !important`}
                >
                  <Box width="100%" height="100%">
                    <Placeholder content={placeholder} error={Boolean(error)} />
                  </Box>
                </Box>
              </Box>
            ) : isVirtualized && data ? (
              <VirtualizedRows
                data={data}
                count={dataLength}
                primaryKey={primaryKey}
                estimateRowHeight={estimateRowHeightPx(rowHeight)}
                renderRow={renderRow}
              />
            ) : (
              rows
            )}
          </Box>
        </Box>
      </Private>
    </SkeletonContextProvider>
  );
}

const getGridTemplateColumns = (columns, defaultMin, defaultMax) =>
  columns
    .map(({ min, max }) => `minmax(${min || defaultMin}, ${max || defaultMax})`)
    .join(" ");

type TableRowProps<RowData extends Record<string, any>> = {
  row: RowData;
  primaryKey: any;
  selected: boolean;
  isHighlighted: boolean;
  highlightColor?: BoxProps["bg"];
  disabled?: boolean | string;
  selectable?: boolean | string;
  checkboxLabel?: string;
  height?: string;
  onClick?: RowClickHandler<RowData>;
  onSelect?: (row: RowData) => unknown;
  onMouseEnter?: (row: RowData) => void;
  onMouseLeave?: (row: RowData) => void;
  columns: TableColumn<RowData>[];
  index: number;
  sortable: boolean;
  isDense: boolean;
  renderExpandedRow?: (row: RowData, rowIndex: number) => ReactNode;
  /** Set when the table's caller controls expansion; the row then shows no toggle. */
  isExpanded?: boolean;
  isLastRow?: boolean;
  measureRef?: (node: Element | null) => void;
};

function TableRow<RowData extends Record<string, any>>({
  row,
  disabled,
  selectable,
  checkboxLabel,
  selected,
  isHighlighted,
  highlightColor,
  height,
  columns,
  onClick,
  onSelect,
  onMouseEnter,
  onMouseLeave,
  sortable,
  isDense,
  index: rowIndex,
  primaryKey,
  renderExpandedRow,
  isExpanded: controlledExpanded,
  isLastRow = false,
  measureRef,
}: TableRowProps<RowData>) {
  const isDisabled = Boolean(disabled);

  const [uncontrolledExpanded, setIsExpanded] = useState(false);
  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : uncontrolledExpanded;
  const expandedContent =
    renderExpandedRow && isExpanded ? renderExpandedRow(row, rowIndex) : null;
  // An expanded last row keeps its border above the panel, like any other row.
  const hideBottomBorder = isLastRow && !expandedContent;

  return (
    <>
      <Row
        primaryKey={primaryKey}
        clickable={Boolean(onClick && !isDisabled)}
        disabled={disabled}
        selectable={selectable}
        checkboxLabel={checkboxLabel}
        row={row}
        selected={selected}
        isHighlighted={isHighlighted}
        highlightColor={highlightColor}
        onClick={!isDisabled ? onClick : undefined}
        onSelect={onSelect}
        onMouseEnter={onMouseEnter ? () => onMouseEnter(row) : undefined}
        onMouseLeave={onMouseLeave ? () => onMouseLeave(row) : undefined}
        sortable={sortable}
        isExpanded={isExpanded}
        setIsExpanded={
          renderExpandedRow && !isControlled ? setIsExpanded : undefined
        }
        rowIndex={rowIndex}
        ariaRowIndex={measureRef ? rowIndex + 1 : undefined}
        hideBottomBorder={hideBottomBorder}
      >
        {columns.map((column: TableColumn, index) => (
          <TableCell
            isDense={isDense}
            key={column.name ? column.name : `column-${index}-cell`}
            rowIndex={rowIndex}
            column={column}
            height={height}
            selected={selected}
            row={row}
            hideBottomBorder={hideBottomBorder}
            measureRef={index === 0 ? measureRef : undefined}
            virtualIndex={index === 0 && measureRef ? rowIndex : undefined}
          />
        ))}
      </Row>
      {expandedContent && (
        <Box as="tr" display="contents">
          <Cell
            sx={{
              // This lets the cell span the entire width of the table.
              gridColumn: `1/-1`,
              bg: "base.lightBackground",
              px: 8,
              py: 6,
            }}
          >
            {expandedContent}
          </Cell>
        </Box>
      )}
    </>
  );
}

const MemoizedTableRow = memo(TableRow);

function isRowHighlighted(
  highlight: TableProps<any>["highlight"],
  key: unknown,
): boolean {
  if (highlight === undefined) return false;
  if (Array.isArray(highlight)) return highlight.some((entry) => entry === key);
  return highlight === key;
}

const createPlaceholderData = (primaryKey: PropertyKey) =>
  Array.from({ length: 10 }).map((_, index) => ({ [primaryKey]: index }));

type WidthBreakpoint = "base" | "sm" | "md" | "lg";

const toWidthBreakpoint = (width: number): WidthBreakpoint =>
  width > 1400 ? "lg" : width > 1000 ? "md" : width > 600 ? "sm" : "base";

/**
 * The table's width as the breakpoint its columns follow, so it re-renders only when it crosses one, not on every resize
 * tick. A sidebar fold sweeps the width every frame; the columns settle once, when it lands, instead of popping mid-slide.
 */
function useWidthBreakpoint(ref: { current: HTMLElement | null }) {
  const [breakpoint, setBreakpoint] = useState<WidthBreakpoint>();
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (isNavFolding()) return;
      const width =
        entry?.borderBoxSize?.[0]?.inlineSize ?? entry?.contentRect.width;
      if (width !== undefined) setBreakpoint(toWidthBreakpoint(width));
    });
    observer.observe(element);
    const removeFoldEnd = onNavFoldEnd(() =>
      setBreakpoint(toWidthBreakpoint(element.offsetWidth)),
    );
    return () => {
      observer.disconnect();
      removeFoldEnd();
    };
  }, [ref]);
  return breakpoint;
}

// Below this row count, windowing costs more than it saves.
const VIRTUALIZE_ROW_THRESHOLD = 30;

// Seeds the virtualizer before rows are measured.
const estimateRowHeightPx = (rowHeight?: string) => {
  const parsed = rowHeight ? Number.parseFloat(rowHeight) : Number.NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 64;
};

type VirtualizedRowsProps<RowData extends Record<string, any>> = {
  data: Array<RowData>;
  count: number;
  primaryKey: keyof RowData;
  estimateRowHeight: number;
  renderRow: (
    row: RowData,
    index: number,
    rowCount: number,
    measureRef?: (node: Element | null) => void,
  ) => ReactNode;
};

// Renders only rows near the viewport, keeping display:contents so cells align to the grid columns.
// Full-width spacer rows fill the off-window height, and the page's scroll, the window's or the card's, drives the virtualizer.
function VirtualizedRows<RowData extends Record<string, any>>({
  data,
  count,
  primaryKey,
  estimateRowHeight,
  renderRow,
}: VirtualizedRowsProps<RowData>) {
  const [scrollMargin, setScrollMargin] = useState(0);
  const spacerRef = useRef<HTMLTableCellElement | null>(null);
  const pageScroll = usePageScroll();

  // display:contents rows have no box, so each row's first cell carries the measure ref.
  const virtualizer = usePageVirtualizer({
    count,
    estimateSize: () => estimateRowHeight,
    overscan: 5,
    scrollMargin,
    // Key by row identity so sorting or filtering does not reuse stale cached heights.
    getItemKey: (index) => data[index]?.[primaryKey] ?? index,
  });

  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // The virtualizer needs the list's offset in the page's scrolled content, which the top spacer provides.
  // It moves whenever content above the table resizes, like a header or a crumb bar folding.
  const renderedRowCount = items.length;
  useLayoutEffect(() => {
    const measure = () => {
      const spacer = spacerRef.current;
      if (!spacer || renderedRowCount === 0) return;
      setScrollMargin(getPageScrollMargin(spacer, pageScroll));
    };
    measure();
    const observer = new ResizeObserver(measure);
    // Beside the product nav the body stays the window's height, so the card's content is what grows above the table.
    observer.observe(pageScroll.element?.firstElementChild ?? document.body);
    return () => observer.disconnect();
  }, [renderedRowCount, pageScroll]);

  const firstItem = items[0];
  const lastItem = items[items.length - 1];
  const paddingTop = firstItem
    ? Math.max(0, firstItem.start - scrollMargin)
    : 0;
  const paddingBottom = lastItem
    ? Math.max(0, totalSize - lastItem.end + scrollMargin)
    : 0;

  return (
    <>
      <Box as="tr" display="contents">
        <Box
          ref={spacerRef}
          as="td"
          gridColumn="1 / -1"
          height={`${paddingTop}px`}
          p={0}
          border="none"
        />
      </Box>
      {items.map((item) => {
        const row = data[item.index];
        return row
          ? renderRow(row, item.index, count, virtualizer.measureElement)
          : null;
      })}
      <Box as="tr" display="contents">
        <Box
          as="td"
          gridColumn="1 / -1"
          height={`${paddingBottom}px`}
          p={0}
          border="none"
        />
      </Box>
    </>
  );
}
